import { Map as IMap, List as IList } from 'immutable';
import { createSelector } from 'reselect';

import {
    messages
} from '../intl/intlSelectors';

import { METHOD_REQUEST_STATES } from './shippingModule';
import {
    getAllPhysicalCards,
    getBuyNowEGiftCards,
    getBuyNowPhysicalGiftCards
} from '../productCartHandler/productCartSelectors';
import { getShippingCostDisplay } from './ShippingMethodUtils';
import { maybeGetDisplayName } from './shippingMessages';

const sortByCarrier = (methodA, methodB) => (
    methodA.get('carrier').localeCompare(methodB.get('carrier'))
);

export const sortByCost = (methodA, methodB) => (
    parseFloat(methodA.get('cost')) > parseFloat(methodB.get('cost')) ? 1 : -1
);

export const getShippingProfiles = state => state.get('shippingMethods', IMap());

export const getShippingMethodProfile = (state, profile) => (
    state.getIn(['shippingMethods', profile], IMap())
);

export const getLastShippingMethods = createSelector(
    getShippingProfiles,
    (shippingProfiles = IMap()) => {
        const profileKeys = shippingProfiles.keySeq().filter(
            key => shippingProfiles.getIn([key, 'methods'])
        );

        const profileKeyValue = shippingProfiles.get('profileKey');
        const lastKey = profileKeyValue === null ? profileKeys.last() : profileKeyValue;
        const methods = shippingProfiles.getIn([lastKey, 'methods'], IList());
        return methods.sortBy(method => parseFloat(method.get('cost') || '0'));
    }
);

export const getShippingMethods = createSelector(
    getShippingMethodProfile,
    methods => methods.get('methods', IList())
);

export const getProfileByCarrier = createSelector(
    getShippingMethods,
    shippingMethods => shippingMethods.sort(-sortByCarrier)
);

export const getProfileLowestCostMethod = createSelector(
    getShippingMethods,
    methods => methods.sort(sortByCost).get(0)
);

export const getMethodStatus = createSelector(
    getShippingMethodProfile,
    profile => profile.get('status')
);

export const getMethodErrorStatus = createSelector(
    getShippingMethodProfile,
    profile => profile.get('errorStatus')
);

export const hasProfile = createSelector(
    getMethodStatus,
    status => status === METHOD_REQUEST_STATES.get('SUCCESS')
);

export const getShippingMethod = (state, profile, token) => {
    if (state.hasIn(['shippingMethods', profile, 'methods'])) {
        return state.getIn(['shippingMethods', profile, 'methods']).find(
            method => method.get('token') === token
        );
    }
    return null;
};

export const hasActiveRequest = createSelector(
    getShippingProfiles,
    profiles => profiles.reduce(
        (result, profile) => result || profile.get('status') === METHOD_REQUEST_STATES.get('ACTIVE'),
        false
    )
);

export const getShippingMethodProfileId = (addressID, quantity, currency, hasAccessory) => (
    `${addressID}_${currency}_${quantity > 1 ? 'multi' : 'single'}${hasAccessory ? '_has_accessory' : ''}`
);

export const getItemShippingMethodProfile = item => (
    getShippingMethodProfileId(
        item.get('addressID'),
        item.get('quantity'),
        item.get('currencyCode'),
        !!item.get(false)
    )
);

export const getShippingRules = createSelector(
    messages,
    msgs => msgs.get('shippingMethod.messages.shippingRules.md', '')
);

/**
 * Returns the selected shipping method token or method object.
 * @param {Immutable.Map} state - Redux state
 * @returns {Immutable.Map|null} - Selected shipping method or null if not found.
 */
export const getSelectedShippingToken = (state) => {
    const shippingMethods = state.get('shippingMethods');
    if (shippingMethods) {
        const selectedShippingMethod = shippingMethods.get('selectedShippingMethod');
        if (selectedShippingMethod) {
            const method = selectedShippingMethod.get
                ? selectedShippingMethod.get('method')
                : selectedShippingMethod.method;
            return method;
        }
    }
    return null;
};

export const getRemovedTokens = (state) => {
    const physicalCards = getAllPhysicalCards(state);

    return physicalCards
        .filter(card => card.get('removed') === true && card.get('shippingAddress'))
        .map((card) => {
            const address = card.get('shippingAddress');
            return address ? address.get('token') : null;
        })
        .filter(token => token !== null);
};

export const getSelectedShippingMethod = state => state.getIn(['shippingMethods', 'selectedShippingMethod', 'method']);

const groupMethodsByToken = (methods) => {
    const grouped = {};
    methods.forEach((method) => {
        const token = method.get('token');
        const cost = parseFloat(method.get('cost')) || 0;
        const quantity = method.get('quantity') || 1;
        const displayName = method.get('displayName') || '';

        if (!grouped[token]) {
            grouped[token] = {
                method, cost, quantity, displayName
            };
        } else {
            grouped[token].quantity += quantity;
        }
    });
    return grouped;
};

/**
 * Builds a mapping of removed token counts.
 * @param {string[]} removedTokens - List of removed tokens
 * @returns {Object} - Mapping of token strings to number of times they were removed.
 */
const getRemovedTokenCounts = removedTokens => removedTokens.reduce((acc, token) => {
    acc[token] = (acc[token] || 0) + 1;
    return acc;
}, {});

/**
 * Builds a shipping summary item.
 *
 * @param {string} token - Token identifier for the shipping method.
 * @param {Immutable.Map} method - Immutable Map representing the shipping method details.
 * @param {number} cost - Cost per unit for the shipping method.
 * @param {number} quantity - Number of units for which shipping is applied.
 *
 * @returns {{
 *   token: string,
 *   adjustedQuantity: number,
 *   displayName: string,
 *   currencyCode: string,
 *   cost: number
 * }} Summary item for the shipping method.
 */
const buildShippingSummaryItem = (token, method, cost, quantity) => ({
    token,
    adjustedQuantity: quantity,
    displayName: method.get('displayName'),
    currencyCode: method.get('currencyCode'),
    cost: parseFloat((cost * quantity).toFixed(2)),
    carrier: method.get('carrier'),
    methodName: method.get('methodName')
});


export const getShippingSummary = (state) => {
    const shippingMethods = getShippingProfiles(state);
    if (!shippingMethods) return null;

    const selectedShippingMethod = shippingMethods.get('selectedShippingCart');
    if (!selectedShippingMethod) return null;

    let methods = selectedShippingMethod.get('method');
    if (!IList.isList(methods)) {
        methods = IList([methods]);
    }

    if (!methods || methods.size === 0 || methods.some(method => method === null)) return null;

    const buyNowPhysicalCards = getBuyNowPhysicalGiftCards(state);
    const buyNowEGiftCards = getBuyNowEGiftCards(state);

    
    if (buyNowEGiftCards?.size > 0) {
        return {
            totalCost: '0.00',
            quantity: 0,
            tokenSummaries: []
        };
    }

    
    if (buyNowPhysicalCards?.size > 0) {
        const selectedMethod = getSelectedShippingMethod(state);
        if (!selectedMethod) return null;

        const token = selectedMethod.get('token');
        const cost = parseFloat(selectedMethod.get('cost')) || 0;

        const summary = buildShippingSummaryItem(token, selectedMethod, cost, 1);

        return {
            totalCost: summary.cost.toFixed(2),
            quantity: 1,
            tokenSummaries: [summary]
        };
    }

    const groupedByToken = groupMethodsByToken(methods);
    const removedTokenCounts = getRemovedTokenCounts(getRemovedTokens(state));

    let totalCost = 0;
    let totalQuantity = 0;
    const tokenSummaries = [];

    Object.entries(groupedByToken).forEach(([token, { method, cost, quantity }]) => {
        const removedCount = removedTokenCounts[token] || 0;
        const adjustedQuantity = Math.max(quantity - removedCount, 0);

        const summary = buildShippingSummaryItem(token, method, cost, adjustedQuantity);
        totalCost += summary.cost;
        totalQuantity += adjustedQuantity;
        tokenSummaries.push(summary);
    });

    return {
        totalCost: totalCost.toFixed(2),
        quantity: totalQuantity,
        tokenSummaries
    };
};


export const getShippingDisplayName = (state, intl) => {
    const shippingMethods = state?.getIn(['shippingMethods', 'selectedShippingCart', 'method']);

    const methodList = IList.isList(shippingMethods) ? shippingMethods : IList([shippingMethods]);
    const displayMap = {};
    if (!intl) {
        return displayMap;
    }

    methodList.forEach((method) => {
        const token = method?.get('token');
        if (token) {
            const {
                cost, carrier, methodName, displayName, currencyCode
            } = method.toJS();
            const displayCost = getShippingCostDisplay(cost, intl, currencyCode);
            const maybeDisplayName = maybeGetDisplayName(carrier, methodName);
            const displayText = maybeDisplayName ? `${intl.formatMessage(maybeDisplayName.toJS())}` : `${displayName}`;
            displayMap[token] = `${displayCost} ${displayText}`;
            displayMap[`${token}_displayCost`] = displayCost;
            displayMap[`${token}_displayText`] = `${displayText}`;
        }
    });

    return displayMap;
};


export const getIsShippingApiWorking = (state) => {
    const shippingMethods = state.get('shippingMethods');
    return shippingMethods
        ? shippingMethods.getIn(['shippingApiStatus', 'isWorking'])
        : false;
};

export const getShippingRetryOption = (state) => {
    const shippingMethods = state.get('shippingMethods');

    if (!shippingMethods) return false;

    const lastUserTriggered = shippingMethods.getIn(['shippingApiStatus', 'lastUserTriggered']);
    const retryCount = shippingMethods.getIn(['shippingApiStatus', 'retryCount']) || 0;

    return lastUserTriggered && retryCount < 4;
};


export const getSelectedShippingCartMethod = (state) => {
    const shippingMethods = state?.getIn(['shippingMethods', 'selectedShippingCart', 'method']);
    if (!shippingMethods) return {};
    return shippingMethods;
};
